import React, {useContext} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import MoleculeTabs, {MoleculeTab, moleculeTabsTypes, moleculeTabsVariants} from '@s-ui/react-molecule-tabs'
import ListBasic from '@adv-mt/mt-list-basic'
import ListModelDetails, {listTypes} from '@adv-mt/mt-list-model-details'

import {AccordionView} from './AccordionView.js'
import {
  BASE_CLASS,
  BASE_TEST_ID,
  CTA_TYPES,
  DISPLAY_TYPES,
  GROUP_KEY_BASIC_DATA_SHEET,
  SECTION_LABEL_MAP
} from './settings.js'

const PanelEquipment = ({
  cta,
  equipment = {basicDataSheet: [], standard: [], optional: []},
  displayType = DISPLAY_TYPES.tabs,
  onClickCTALink = () => {}
}) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')

  const hasEquipment = Boolean(Object.keys(equipment).length)
  const hasCTA = Boolean(cta)
  const isAccordion = displayType === DISPLAY_TYPES.accordion
  const ctaIsButton = hasCTA && cta.type === CTA_TYPES.button
  const ctaDesign = hasCTA && (cta.design || atomButtonDesigns.SOLID)
  const ctaShape = atomButtonShapes.CIRCULAR

  if (!hasEquipment) return <></>

  const optionalProps = isMobile && {type: moleculeTabsTypes.FULLWIDTH}

  const handleCTAClick = () => !ctaIsButton && onClickCTALink()

  if (isAccordion) {
    return <AccordionView equipment={equipment} />
  }
  return (
    <div className={BASE_CLASS}>
      <MoleculeTabs variant={moleculeTabsVariants.HIGHLIGHTED} {...optionalProps}>
        {Object.keys(equipment).map((section, i) => {
          const isBasicDataSheetTab = section === GROUP_KEY_BASIC_DATA_SHEET
          const isFirst = i === 0
          const label = i18n.t(SECTION_LABEL_MAP[section])
          const [modelDetails] = isBasicDataSheetTab ? equipment.basicDataSheet : []
          const contentTestId = `${BASE_TEST_ID}-${section}`
          return (
            <MoleculeTab
              numTab={i + 1}
              label={<span className={`${BASE_CLASS}-tabLabel`}>{label}</span>}
              key={i}
              active={isFirst}
            >
              {equipment[section]?.map(({title, descriptions}, i) => (
                <div className={`${BASE_CLASS}-group`} key={i} data-testid={contentTestId}>
                  {isBasicDataSheetTab ? (
                    <ListModelDetails displayType={listTypes.icon} modelDetails={modelDetails} withWrapper={false} />
                  ) : (
                    <ListBasic title={title} items={descriptions} />
                  )}
                </div>
              ))}
            </MoleculeTab>
          )
        })}
      </MoleculeTabs>
      {hasCTA && (
        <div className={`${BASE_CLASS}-cta`}>
          <AtomButton
            design={ctaDesign}
            href={cta.url}
            link={!ctaIsButton}
            onClick={handleCTAClick}
            shape={ctaShape}
            type={cta.type}
          >
            {cta.text}
          </AtomButton>
        </div>
      )}
    </div>
  )
}

PanelEquipment.displayName = 'PanelEquipment'
PanelEquipment.propTypes = {
  cta: PropTypes.shape({
    design: PropTypes.oneOf(Object.keys(atomButtonDesigns)),
    text: PropTypes.text,
    type: PropTypes.oneOf(Object.values(CTA_TYPES)),
    url: PropTypes.string
  }),
  equipment: PropTypes.shape({
    standard: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        descriptions: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.string.isRequired
          })
        ).isRequired
      })
    ).isRequired,
    optional: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        descriptions: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.string.isRequired
          })
        ).isRequired
      })
    )
  }).isRequired,
  displayType: PropTypes.oneOf(DISPLAY_TYPES),
  onClickCTALink: PropTypes.func
}

export {
  atomButtonDesigns as panelEquipmentCTADesigns,
  atomButtonShapes as panelEquipmentCTAShapes,
  CTA_TYPES as panelEquipmentCTATypes,
  DISPLAY_TYPES as panelEquipmentDisplayTypes
}

export default PanelEquipment
